import { findCountryName } from "@redotech/locale/countries";
import { supplierIdFormat } from "@redotech/redo-inventory-id";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { DateFromDateOrIsoStringSchema } from "./date-schema";

const LocationForMoveSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  name: z.string().meta({ privacy: privacy.nonPii }),
  movable: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
  message: z.string().nullish().meta({ privacy: privacy.redact }),
  unavailableLineItemsCount: z
    .number()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  selectedByFO: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
  redoOnly: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
});
export const LocationForMoveSchema: z.ZodType<LocationForMove> =
  LocationForMoveSchema_;
export interface LocationForMove extends z.infer<
  typeof LocationForMoveSchema_
> {}

const ShopifyLocationAddressSchema = z.object({
  address1: z.string().nullish().meta({ privacy: privacy.address_line }),
  address2: z.string().nullish().meta({ privacy: privacy.address_line }),
  city: z.string().nullish().meta({ privacy: privacy.nonPii }),
  country: z.string().nullish().meta({ privacy: privacy.nonPii }),
  countryCode: z.string().nullish().meta({ privacy: privacy.nonPii }),
  phone: z.string().nullish().meta({ privacy: privacy.phone }),
  province: z.string().nullish().meta({ privacy: privacy.nonPii }),
  provinceCode: z.string().nullish().meta({ privacy: privacy.nonPii }),
  zip: z.string().nullish().meta({ privacy: privacy.nonPii }),
  company: z.string().nullish().meta({ privacy: privacy.nonPii }),
});

const ShopifyFulfillmentServiceSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  type: z.string().nullish().meta({ privacy: privacy.nonPii }),
  serviceName: z.string().nullish().meta({ privacy: privacy.nonPii }),
});
export const ShopifyFulfillmentServiceSchema: z.ZodType<ShopifyFulfillmentService> =
  ShopifyFulfillmentServiceSchema_;
export interface ShopifyFulfillmentService extends z.infer<
  typeof ShopifyFulfillmentServiceSchema_
> {}

const ShopifyLocationSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  name: z.string().meta({ privacy: privacy.nonPii }),
  address: ShopifyLocationAddressSchema.meta({ privacy: privacy.structured }),
  addressVerified: z.boolean().meta({ privacy: privacy.nonPii }),
  createdAt: z.string().meta({ privacy: privacy.nonPii }),
  fulfillmentService: ShopifyFulfillmentServiceSchema_.nullish().meta({
    privacy: privacy.structured,
  }),
  fulfillsOnlineOrders: z.boolean().meta({ privacy: privacy.nonPii }),
  isActive: z.boolean().meta({ privacy: privacy.nonPii }),
  shipsInventory: z.boolean().meta({ privacy: privacy.nonPii }),
  updatedAt: z.string().meta({ privacy: privacy.nonPii }),
});
export const ShopifyLocationSchema: z.ZodType<ShopifyFulfillmentLocation> =
  ShopifyLocationSchema_;

export interface ShopifyFulfillmentLocation extends z.infer<
  typeof ShopifyLocationSchema_
> {}

const RedoFulfillmentLocationSchema_ = ShopifyLocationSchema_.omit({
  id: true,
}).extend({
  _id: zExt.objectId().nullish().meta({ privacy: privacy.nonPii }),
  provider: createEnum(Provider, { enumName: "Provider" }).meta({
    privacy: privacy.nonPii,
  }),
  externalId: z.string().meta({ privacy: privacy.nonPii }),
  teamId: z.string().meta({ privacy: privacy.nonPii }),
  providerCreatedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }),
  providerUpdatedAt: DateFromDateOrIsoStringSchema.meta({
    privacy: privacy.nonPii,
  }),
  updatedAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
  createdAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
  canonicalLocationExternalId: z
    .string()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  shippingName: z.string().nullish().meta({ privacy: privacy.nonPii }),
  supplierId: supplierIdFormat.schema
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});
export const RedoFulfillmentLocationSchema: z.ZodType<RedoFulfillmentLocation> =
  RedoFulfillmentLocationSchema_;

export interface RedoFulfillmentLocation extends z.infer<
  typeof RedoFulfillmentLocationSchema_
> {}

export interface IRedoFulfillmentLocation extends z.ZodType<RedoFulfillmentLocation> {}

const CreateRedoFulfillmentLocationSchema_ =
  RedoFulfillmentLocationSchema_.omit({
    _id: true,
    updatedAt: true,
    createdAt: true,
  });
export const CreateRedoFulfillmentLocationSchema: z.ZodType<CreateRedoFulfillmentLocation> =
  CreateRedoFulfillmentLocationSchema_;

export interface CreateRedoFulfillmentLocation extends z.infer<
  typeof CreateRedoFulfillmentLocationSchema_
> {}

export function mapShopifyFulfillmentLocation(
  location: ShopifyFulfillmentLocation,
  teamId: string,
): CreateRedoFulfillmentLocation {
  const data = {
    ...location,
    provider: Provider.SHOPIFY,
    externalId: location.id,
    teamId,
    providerCreatedAt: new Date(location.createdAt),
    providerUpdatedAt: new Date(location.updatedAt),
    address: location.address
      ? {
          ...location.address,
          // Shopify often omits the full country name; derive it from countryCode
          // so the `country` column is never blank on new records.
          country:
            location.address.country ??
            (location.address.countryCode
              ? (findCountryName(location.address.countryCode) ?? null)
              : null),
        }
      : location.address,
  };
  return CreateRedoFulfillmentLocationSchema_.parse(data);
}
